import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { Button } from 'react-scale/components/design-system';
import { FastBackButton, BackButton, ForwardButton } from './ToolTipButtons';
import ReactTooltip from 'react-tooltip';
import Kbd from 'react-scale/components/design-system/Kbd';
import _ from 'lodash';
import useShortcut from 'react-scale/hooks/useShortcut';
import { DefaultButtonRowProps } from '../../types';

const useStyles = makeStyles({
  buttonRow: {
    minHeight: 40,
    background: 'var(--color-RemoGray01)',
    padding: '8px 4px',
    display: 'flex',
    alignItems: 'center',
  },
  actionButton: {
    display: 'flex',
    alignSelf: 'center',
    marginLeft: 'auto',
    marginRight: '6px',
    '& span': {
      fontSize: '11px',
    },
  },
  grayButton: {
    backgroundColor: 'gray',
  },
});

export interface WorkflowStepActionButtonProps {
  completedText: string;
  completedStateClick?: () => void;
  startedText?: string;
  startedStateClick?: () => void;
  allChildrenCompletedText?: string;
  allChildrenCompletedStateClick?: () => void;
  uncompletedText: string;
  uncompletedStateClick?: () => void;
}

const DefaultButtonRow: React.FC<DefaultButtonRowProps> = ({
  workflowHook,
  reviewStateHook,
  actionButtonProps = {
    uncompletedText: 'Complete',
    completedText: 'Undo',
  },
}) => {
  const classes = useStyles();

  const { workflowState, toggleCompleted, completeAndMoveNext } = workflowHook;

  const { currentStep } = workflowState;
  const isCompleted = workflowState.completedStepIds.includes(workflowState.activeStepId);
  const numCompletedChildren = currentStep.steps
    ? _.filter(currentStep.steps, step => workflowState.completedStepIds.includes(step.id)).length
    : 0;

  const isStarted = !!(
    currentStep.steps &&
    currentStep.steps.length > 0 &&
    numCompletedChildren > 0
  );
  const isAllChildrenCompleted = !!(
    currentStep.steps &&
    currentStep.steps.length > 0 &&
    numCompletedChildren === currentStep.steps.length
  );

  const toggleComplete: () => void = useCallback(() => {
    toggleCompleted(currentStep.id);
    reviewStateHook?.setAnnotationReviewStateByID(currentStep.id, true);
  }, [currentStep, reviewStateHook, toggleCompleted]);

  const buttonState = {
    isCompleted,
    defaultCompletedStateClick: toggleComplete,
    isAllChildrenCompleted,
    isStarted,
    defaultStateClick: completeAndMoveNext,
  };

  const actionButtonTextAndBehavior = useMemo(
    () => getActionButtonTextAndBehavior(buttonState, actionButtonProps),
    [actionButtonProps, buttonState],
  );

  const onClickHandler: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      actionButtonTextAndBehavior.onClick();
    },
    [actionButtonTextAndBehavior],
  );

  useShortcut({
    trigger: 'shift+space',
    description: 'Perform Recommended Workflow Action',
    action: actionButtonTextAndBehavior.onClick,
  });

  return (
    <div className={classes.buttonRow}>
      <FastBackButton workflowHook={workflowHook} />
      <BackButton workflowHook={workflowHook} />
      <ForwardButton workflowHook={workflowHook} />
      <div
        data-tip
        data-for="ActionButton"
        data-effect="solid"
        data-place="bottom"
        className={classes.actionButton}
      >
        <ReactTooltip id="ActionButton">
          {
            <div>
              <Kbd>SHIFT</Kbd> + <Kbd>Space</Kbd>
            </div>
          }
        </ReactTooltip>
        <Button
          type="primary"
          onClick={onClickHandler}
          className={clsx({}, { [classes.grayButton]: isCompleted || isStarted })}
        >
          {actionButtonTextAndBehavior.text}
        </Button>
      </div>
    </div>
  );
};

export const getActionButtonTextAndBehavior = (
  buttonState: {
    isCompleted: boolean;
    defaultCompletedStateClick: () => void;
    isAllChildrenCompleted: boolean;
    isStarted: boolean;
    defaultStateClick: () => void;
  },
  actionButtonProps: WorkflowStepActionButtonProps,
): { text: string; onClick: () => void } => {
  if (buttonState.isCompleted) {
    return {
      text: actionButtonProps.completedText,
      onClick: actionButtonProps.completedStateClick ?? buttonState.defaultCompletedStateClick,
    };
  } else if (
    actionButtonProps.allChildrenCompletedText &&
    actionButtonProps.allChildrenCompletedStateClick &&
    buttonState.isAllChildrenCompleted
  ) {
    return {
      text: actionButtonProps.allChildrenCompletedText,
      onClick: actionButtonProps.allChildrenCompletedStateClick,
    };
  } else if (
    actionButtonProps.startedText &&
    actionButtonProps.startedStateClick &&
    buttonState.isStarted
  ) {
    return {
      text: actionButtonProps.startedText,
      onClick: actionButtonProps.startedStateClick,
    };
  } else {
    return {
      text: actionButtonProps.uncompletedText,
      onClick: actionButtonProps.uncompletedStateClick ?? buttonState.defaultStateClick,
    };
  }
};

export default React.memo(DefaultButtonRow);
